import {
  GeneratedMutation,
  GeneratedQuery,
  GraphQLOptionsV6,
  GraphQLResponseV6,
  GraphQLResult,
  GraphqlSubscriptionResult,
  V6Client,
} from "@aws-amplify/api-graphql";
import { Observable } from "rxjs";
import { isAuthCredentialError } from "~/auth/authErrors";
import { triggerAndAwaitTokenRefresh } from "~/auth/awsUtils";
import { errorReporter } from "~/error-handling/errorReporter";
import {
  createGraphQLError,
  GraphQLErrorLike,
  parseSubscriptionError,
  partitionErrors,
} from "~/error-handling/graphqlErrors";
import { amplifyGraphQLClient } from "../amplify-client";

/**
 * Extract the output type from a GeneratedQuery or GeneratedMutation.
 * Falls back to FALLBACK if the string is not a branded query type.
 */
type ExtractOutputType<FALLBACK, TYPED_GQL_STRING extends string> =
  TYPED_GQL_STRING extends GeneratedQuery<infer _IN, infer OUT>
    ? OUT
    : TYPED_GQL_STRING extends GeneratedMutation<infer _IN, infer OUT>
      ? OUT
      : FALLBACK;

/**
 * Error handling modes for GraphQL operations
 *
 * - "strict": Default. Throws on ANY error (matches Amplify default behavior)
 * - "partial": Throws only on fatal errors, allows partial data with auth errors
 * - "silent": Never throws, returns all errors in result for caller to handle
 */
export type ErrorMode = "partial" | "strict" | "silent";

export interface GraphQLClientOptions {
  errorMode?: ErrorMode;
}

/** Standard GraphQL options (same as V6Client.graphql) */
export type GraphQLOptions = Parameters<typeof amplifyGraphQLClient.graphql>[0];

export interface GraphQLResultWithMeta<T> {
  data: T | null;
  /** Errors that were suppressed (e.g., auth errors on nested fields) */
  partialErrors: GraphQLErrorLike[];
  /** Errors that would cause a throw in non-silent mode */
  fatalErrors: GraphQLErrorLike[];
}

/**
 * Wrapper for Amplify GraphQL client with intelligent error handling.
 *
 * @see portal/src/lib/README.md for full documentation
 *
 * @example
 * // Default (strict) - throws on any error (matches Amplify behavior)
 * const { data } = await graphql({ query, variables });
 *
 * @example
 * // Partial - allows partial data, throws only on fatal errors
 * const { data } = await graphql({ query }, { errorMode: "partial" });
 *
 * @example
 * // Silent - never throws, handle errors yourself
 * const { data, fatalErrors } = await graphql({ query }, { errorMode: "silent" });
 * if (fatalErrors.length > 0) { ... }
 */
/**
 * Type guard to check if an error is a GraphQL response with data and errors.
 * Amplify throws/rejects even when there's partial data with auth errors.
 */
function isGraphQLResponseWithErrors<T>(
  error: unknown,
): error is GraphQLResult<T> & { errors: GraphQLErrorLike[] } {
  return (
    error !== null &&
    typeof error === "object" &&
    "data" in error &&
    "errors" in error &&
    Array.isArray((error as { errors: unknown }).errors)
  );
}

/**
 * Process a GraphQL response (either from success path or caught rejection)
 * and handle errors based on the error mode.
 */
function processResponse<T>(
  response: GraphQLResult<T> & { errors?: GraphQLErrorLike[] },
  errorMode: ErrorMode,
): GraphQLResultWithMeta<T> {
  const { partial, fatal } = partitionErrors(response.errors);

  // Handle based on error mode
  if (errorMode === "strict" && (partial.length > 0 || fatal.length > 0)) {
    throw createGraphQLError([...partial, ...fatal], [], "GraphQL errors");
  }

  if (errorMode === "partial" && fatal.length > 0) {
    throw createGraphQLError(fatal, partial, "GraphQL errors");
  }

  // Log suppressed partial errors for visibility
  if (partial.length > 0) {
    console.debug(
      `[GraphQL] ${partial.length} partial error(s) (auth) - data returned successfully`,
    );

    // Report partial errors to Sentry at debug level for monitoring auth patterns
    errorReporter.reportMessage(
      `GraphQL: ${partial.length} partial error(s) suppressed`,
      {
        level: "debug",
        tags: { service: "graphql", errorType: "partial_auth" },
        extra: {
          errorCount: partial.length,
          paths: partial.map((e) => e.path?.join(".")).filter(Boolean),
          errors: partial.map((e) => ({
            message: e.message,
            path: e.path,
            errorType: e.errorType ?? e.extensions?.errorType,
          })),
        },
      },
    );
  }

  return {
    data: response.data ?? null,
    partialErrors: partial,
    fatalErrors: fatal,
  };
}

export async function graphql<
  FALLBACK_TYPES = unknown,
  TYPED_GQL_STRING extends string = string,
>(
  options: GraphQLOptionsV6<FALLBACK_TYPES, TYPED_GQL_STRING>,
  { errorMode = "strict" }: GraphQLClientOptions = {},
): Promise<
  GraphQLResultWithMeta<ExtractOutputType<FALLBACK_TYPES, TYPED_GQL_STRING>>
> {
  type OutputType = ExtractOutputType<FALLBACK_TYPES, TYPED_GQL_STRING>;
  try {
    const response = (await amplifyGraphQLClient.graphql(
      options,
    )) as GraphQLResult<OutputType>;
    return processResponse(response, errorMode);
  } catch (error) {
    // Amplify throws/rejects even when there's partial data with auth errors.
    // Check if the "error" is actually a response with data + errors.
    if (isGraphQLResponseWithErrors<OutputType>(error)) {
      return processResponse(error, errorMode);
    }

    // Re-throw non-GraphQL errors (network errors, etc.)
    throw error;
  }
}

/**
 * Run a query through `graphql()`, and on an auth/credential failure
 * (`NoSignedUser` / "No current user" / expired token) trigger ONE deduped
 * token refresh and retry exactly once.
 *
 * This is the GraphQL analogue of `withAWSRetry` for AWS SDK calls: mid-session
 * the Cognito Identity-Pool credentials can go stale (e.g. laptop sleep → tokens
 * expire → every query refetches on wake), and without recovery the failing
 * queries loop via react-query retries (the NoSignedUser white-screen cascade).
 * The refresh is single-flighted in AuthSessionProvider, so concurrent failing
 * queries collapse onto one Cognito call. If the refresh fails, the original
 * error is surfaced and configureAuthSession handles a dead session by
 * redirecting to /login.
 *
 * Mutations are intentionally NOT routed here (replay risk) — only queries.
 */
async function graphqlWithAuthRecovery<
  FALLBACK_TYPES = unknown,
  TYPED_GQL_STRING extends string = string,
>(
  options: GraphQLOptionsV6<FALLBACK_TYPES, TYPED_GQL_STRING>,
  clientOptions: GraphQLClientOptions | undefined,
): Promise<
  GraphQLResultWithMeta<ExtractOutputType<FALLBACK_TYPES, TYPED_GQL_STRING>>
> {
  try {
    return await graphql<FALLBACK_TYPES, TYPED_GQL_STRING>(
      options,
      clientOptions,
    );
  } catch (error) {
    if (!isAuthCredentialError(error)) {
      throw error;
    }

    errorReporter.addFeatureBreadcrumb("graphql-auth-recovery", "triggered", {
      error: error.message,
    });

    try {
      await triggerAndAwaitTokenRefresh();
    } catch {
      // Refresh failed → genuinely dead session. Surface the original error;
      // configureAuthSession has already begun the clear + /login redirect.
      errorReporter.addFeatureBreadcrumb("graphql-auth-recovery", "failed", {
        reason: "refresh-failed",
      });
      throw error;
    }

    try {
      const result = await graphql<FALLBACK_TYPES, TYPED_GQL_STRING>(
        options,
        clientOptions,
      );
      errorReporter.addFeatureBreadcrumb("graphql-auth-recovery", "recovered");
      return result;
    } catch (retryError) {
      errorReporter.addFeatureBreadcrumb("graphql-auth-recovery", "failed", {
        reason: "retry-failed",
      });
      throw retryError;
    }
  }
}

/**
 * Check if a GraphQL query string is a subscription operation.
 */
function isSubscriptionQuery(query: string): boolean {
  return /^\s*subscription[\s({]/i.test(query);
}

/**
 * Check if a GraphQL query string is a mutation operation. Mutations are
 * excluded from auth refresh-and-retry to avoid replaying a write.
 */
function isMutationQuery(query: string): boolean {
  return /^\s*mutation[\s({]/i.test(query);
}

/**
 * Handle a subscription error by classifying it and either suppressing (auth)
 * or propagating (fatal/unknown) to the subscriber.
 */
function handleSubscriptionError(
  err: unknown,
  subscriber: { error: (err: unknown) => void; complete: () => void },
): void {
  const errors = parseSubscriptionError(err);
  const { partial, fatal } = partitionErrors(errors);

  if (fatal.length > 0) {
    // Real errors — propagate to consumer
    subscriber.error(err);
  } else if (partial.length > 0) {
    // Auth-only errors — suppress and log
    console.debug(
      `[GraphQL Subscription] ${partial.length} auth error(s) suppressed`,
    );

    errorReporter.reportMessage(
      `GraphQL Subscription: ${partial.length} auth error(s) suppressed`,
      {
        level: "debug",
        tags: { service: "graphql", errorType: "subscription_auth" },
        extra: {
          errorCount: partial.length,
          errors: partial.map((e) => ({
            message: e.message,
            errorType: e.errorType ?? e.extensions?.errorType,
          })),
        },
      },
    );

    // Complete the subscription so it doesn't hang in limbo
    subscriber.complete();
  } else {
    // Unknown error format — propagate to be safe
    subscriber.error(err);
  }
}

/**
 * Wrap an Amplify subscription observable with error classification.
 * Auth errors are suppressed and logged; fatal errors are propagated.
 */
function wrapSubscription<T>(
  observable: GraphqlSubscriptionResult<T>,
): GraphqlSubscriptionResult<T> {
  return new Observable((subscriber) => {
    const subscription = observable.subscribe({
      next: (value) => subscriber.next(value),
      error: (err: unknown) => handleSubscriptionError(err, subscriber),
      complete: () => subscriber.complete(),
    });

    return () => subscription.unsubscribe();
  });
}

/**
 * GraphQL client wrapper type that extends V6Client with optional errorMode.
 * Services should use this type instead of V6Client directly.
 *
 * The graphql method preserves Amplify's type inference from query strings
 * while adding an optional second parameter for error mode configuration.
 */
export interface GraphQLClientWrapper extends Omit<V6Client, "graphql"> {
  graphql: <FALLBACK_TYPES = unknown, TYPED_GQL_STRING extends string = string>(
    options: GraphQLOptionsV6<FALLBACK_TYPES, TYPED_GQL_STRING>,
    clientOptions?: GraphQLClientOptions,
  ) => GraphQLResponseV6<FALLBACK_TYPES, TYPED_GQL_STRING>;
}

/**
 * V6Client-compatible wrapper with intelligent error handling.
 * Drop-in replacement for amplifyGraphQLClient in services.
 *
 * Supports queries, mutations, and subscriptions:
 * - Queries/mutations: error mode controls throw behavior (strict/partial/silent)
 * - Subscriptions: auth errors are suppressed and logged, fatal errors propagate
 *
 * @example
 * // In a service file:
 * import { graphqlClient } from "~/lib/graphql-client";
 * export const myService = MyService.getInstance(graphqlClient);
 *
 * @example
 * // Default (strict) - throws on any error
 * const response = await this.client.graphql({ query, variables });
 *
 * @example
 * // Partial - allows partial data with auth errors
 * const response = await this.client.graphql(
 *   { query, variables },
 *   { errorMode: "partial" }
 * );
 *
 * @example
 * // Subscription - auth errors suppressed automatically
 * const sub = (this.client.graphql({ query: onUpdateItem }) as Observable).subscribe({
 *   next: (result) => { ... },
 *   error: (err) => { // only fatal errors reach here },
 * });
 */
export const graphqlClient: GraphQLClientWrapper = {
  ...amplifyGraphQLClient,
  graphql: (<
    FALLBACK_TYPES = unknown,
    TYPED_GQL_STRING extends string = string,
  >(
    options: GraphQLOptionsV6<FALLBACK_TYPES, TYPED_GQL_STRING>,
    clientOptions?: GraphQLClientOptions,
  ): GraphQLResponseV6<FALLBACK_TYPES, TYPED_GQL_STRING> => {
    // Route subscriptions through the subscription wrapper
    if (
      typeof options.query === "string" &&
      isSubscriptionQuery(options.query)
    ) {
      const observable = amplifyGraphQLClient.graphql(
        options,
      ) as unknown as GraphqlSubscriptionResult<FALLBACK_TYPES>;
      return wrapSubscription(observable) as unknown as GraphQLResponseV6<
        FALLBACK_TYPES,
        TYPED_GQL_STRING
      >;
    }

    type OutputType = ExtractOutputType<FALLBACK_TYPES, TYPED_GQL_STRING>;
    // Queries get auth refresh-and-retry recovery; mutations skip it (replay
    // risk) and go straight through the error-handling wrapper. Amplify Gen 1
    // codegen always emits string queries, so the typeof guard holds in
    // practice; a non-string DocumentNode would fall to the query path.
    const isMutation =
      typeof options.query === "string" && isMutationQuery(options.query);
    const source = isMutation
      ? graphql<FALLBACK_TYPES, TYPED_GQL_STRING>(options, clientOptions)
      : graphqlWithAuthRecovery<FALLBACK_TYPES, TYPED_GQL_STRING>(
          options,
          clientOptions,
        );
    const promise = source.then(
      (result) =>
        ({
          data: result.data,
          errors:
            result.partialErrors.length > 0 ? result.partialErrors : undefined,
        }) as GraphQLResult<OutputType>,
    );

    return promise as GraphQLResponseV6<FALLBACK_TYPES, TYPED_GQL_STRING>;
  }) as GraphQLClientWrapper["graphql"],
};
